/**
 * Carry a user's SAVED grid state across a column `field` rename.
 *
 * A grid's saved layout (`/v6/filters/display/<name>`) and saved sort
 * (`/v6/sort/<name>`) are keyed by column `field`. MUI's restore keeps only the
 * `orderedFields` that still name a column and appends every other column at
 * the END (x-data-grid 6.9 gridColumnsUtils `applyInitialState`), so renaming a
 * field silently moves that column to the far right and drops its saved width
 * and visibility, for every user who ever saved a layout. The saved sort keeps
 * asking the server for the old field.
 *
 * `renamedFields` maps OLD field -> NEW field. It is applied to what was READ
 * from storage, before it reaches the grid; the next save writes the new keys.
 * When a saved record already holds the new key, the new key wins.
 *
 * Saved FILTERS are deliberately not renamed: a filter value is typed by the
 * field it was written for, and a rename usually changes that type (the
 * Flights Aircraft column moved from the integer `aircraft_id`, which its
 * advanced-search picker still filters, to `aircraft_registration`).
 */
import type { GridSortModel } from "@mui/x-data-grid-pro";

export type RenamedFields = Record<string, string>;

type SavedColumns = {
  orderedFields?: string[];
  dimensions?: Record<string, unknown>;
  columnVisibilityModel?: Record<string, boolean>;
  [key: string]: unknown;
};

function renameRecord<V>(
  record: Record<string, V> | undefined,
  renamed: RenamedFields
): Record<string, V> | undefined {
  if (!record) return record;
  const out: Record<string, V> = {};
  for (const [field, value] of Object.entries(record)) {
    const to = renamed[field];
    if (to === undefined) {
      out[field] = value;
    } else if (!(to in record)) {
      out[to] = value;
    }
  }
  return out;
}

/** The saved `columns` state with every renamed field under its new name. */
export function renameSavedColumns<S extends SavedColumns>(
  columns: S,
  renamed: RenamedFields | undefined
): S {
  if (!renamed || !columns || Object.keys(renamed).length === 0) return columns;
  const out = { ...columns };
  if (Array.isArray(columns.orderedFields)) {
    const seen = new Set<string>();
    out.orderedFields = columns.orderedFields
      .map((field) => renamed[field] ?? field)
      .filter((field) => (seen.has(field) ? false : (seen.add(field), true)));
  }
  if (columns.dimensions) out.dimensions = renameRecord(columns.dimensions, renamed);
  if (columns.columnVisibilityModel) {
    out.columnVisibilityModel = renameRecord(columns.columnVisibilityModel, renamed);
  }
  return out;
}

/** The saved sort model with every renamed field under its new name. */
export function renameSortModel(
  sortModel: GridSortModel,
  renamed: RenamedFields | undefined
): GridSortModel {
  if (!renamed || !Array.isArray(sortModel)) return sortModel;
  return sortModel.map((item) =>
    renamed[item.field] ? { ...item, field: renamed[item.field] } : item
  );
}
